import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  Chip,
  Stack,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid, GridOverlay } from "@mui/x-data-grid";
import { Info } from "@mui/icons-material";
import MaterialTypography from "../MaterialTypography";

const CustomSearchOverlay = () => (
  <GridOverlay>
    <Stack alignItems="center" justifyContent="center" spacing={1}>
      <CircularProgress size={20} />
      <Typography variant="body2" color="textSecondary">
        Searching...
      </Typography>
    </Stack>
  </GridOverlay>
);

const NoResultsOverlay = () => (
  <GridOverlay>
    <Typography variant="body2" color="textSecondary">
      No matching records found.
    </Typography>
  </GridOverlay>
);

const MaterialDataGrid = ({
  rows,
  columns,
  checkboxSelection,
  onRowClick,
  customHeight,
  loading,
  localeText,
  isSelectable,
}) => {
  const [searchText, setSearchText] = useState("");
  const [filteredRows, setFilteredRows] = useState(rows);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (searchText.length > 0) {
      setIsSearching(true);
      const timeout = setTimeout(() => {
        const lower = searchText.toLowerCase();
        const filtered = rows.filter((row) =>
          Object.values(row).some((value) =>
            String(value).toLowerCase().includes(lower)
          )
        );
        setFilteredRows(filtered);
        setIsSearching(false);
      }, 500);
      return () => clearTimeout(timeout);
    } else {
      setFilteredRows(rows);
      setIsSearching(false);
    }
  }, [searchText, rows]);

  const columnsData = columns.map((column) => ({
    field: column.field,
    headerName:
      typeof column.headerName === "string" ? column.headerName : column.field,
    renderHeader:
      column.headerName && typeof column.headerName === "string"
        ? () => (
            <MaterialTypography
              variant="caption"
              color="black"
              fontWeight="medium"
              sx={({ typography: { size } }) => ({ fontSize: size.sm })}
            >
              {column.headerName}
            </MaterialTypography>
          )
        : undefined,
    flex: column.flex,
    width: column.width,
    sortable: true,
    renderCell: (params) =>
      column.type === "status" ? (
        <Stack
          direction="row"
          alignItems="center"
          textAlign="center"
          gap={1}
          mt={1.5}
        >
          <Chip
            label={params.row.status?.label}
            size="small"
            icon={params.row.status?.icon || <Info />}
            color={params.row.status?.color}
          />
        </Stack>
      ) : column.type === "images" ? (
        <img className="image" src={params.row.image} alt="column image" />
      ) : (
        <MaterialTypography
          variant="caption"
          color="dark"
          sx={({ typography: { size } }) => ({ fontSize: size.sm })}
        >
          {params.row[column.field]}
        </MaterialTypography>
      ),
  }));
  return (
    <Box>
      <Box width="16rem" ml="auto" p={1}>
        <TextField
          fullWidth
          autoFocus
          variant="outlined"
          size="small"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {isSearching ? (
                  <CircularProgress color="success" size={18} />
                ) : (
                  <SearchIcon fontSize="small" />
                )}
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box style={{ height: customHeight || 410, width: "100%" }}>
        <DataGrid
          rowHeight={35}
          rows={filteredRows}
          columns={columnsData}
          loading={loading || isSearching}
          checkboxSelection={checkboxSelection}
          onRowClick={onRowClick}
          disableRowSelectionOnClick
          disableColumnMenu={!isSelectable}
          pagination={10}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          localeText={localeText}
          density="standard"
          slots={{
            loadingOverlay: CustomSearchOverlay,
            noRowsOverlay:
              searchText && !isSearching && filteredRows.length === 0
                ? NoResultsOverlay
                : undefined,
          }}
          sx={{
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
              width: "1px",
              height: "1px",
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
              backgroundColor: "grey.400",
              borderRadius: "10px",
            },
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
          }}
        />
      </Box>
    </Box>
  );
};

MaterialDataGrid.propTypes = {
  rows: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  checkboxSelection: PropTypes.bool,
  isSelectable: PropTypes.bool,
  loading: PropTypes.bool,
  onRowClick: PropTypes.func,
  localeText: PropTypes.object,
  customHeight: PropTypes.number,
};

export default MaterialDataGrid;
